import { useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export interface AssessmentReport {
  id: string;
  meta: MetaData;
  sections: Section[];
  extracted_files_overview: ExtractedFile[];
  created?: string;
}

export interface MetaData {
  max_score: number;
  supplier_id: string;
  total_score: number;
  assessment_id: string;
  supplier_name: string;
  template_name: string;
  overall_percentage: number;
  tags: string[];
  industry_average_percentage?: number;
  size_average_percentage?: number;
  others_average?: number;
}

export interface Section {
  id: string;
  score: number;
  title: string;
  groups: Group[];
  max_score: number;
  percentage: number;
  file_answers: FileAnswer[];
  description?: string;
  exposed_answers: QuestionAnswer[];
}

export interface QuestionAnswer {
  question_id: string;
  question_key: string;
  question_title: ReactNode;
  question_type: string;
  value: string | number | boolean;
}

export interface Group {
  score: number;
  title: string;
  max_score: number;
  percentage: number;
}

export interface FileAnswer {
  question_title: ReactNode;
  file_value: string;
  question_key: string;
}

export interface ExtractedFile {
  section: string;
  file_value: string;
  question_key: string;
}

export function useAssessmentReport(assessmentId: string | undefined) {
  const [report, setReport] = useState<AssessmentReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!assessmentId) return;

    supabase
      .from('assessment_reports')
      .select('report_data, created_at') // question_title
      .eq('assessment_id', assessmentId)
      .single()
      .then(({ data }) => {
        setReport({ ...data?.report_data, created: data?.created_at });
        setLoading(false);
      });
  }, [assessmentId]);

  return { report, loading };
}

export async function getPublishedReportById(reportId: string) {
  const { data, error } = await supabase
    .from('published_reports')
    .select('report_data, published_at, created_at, status, id')
    .eq('id', reportId)
    .single();

  if (error) {
    console.error('Error fetching published report:', error);
    return null;
  }

  return {
    ...data?.report_data,
    published: data?.published_at,
    created: data?.created_at,
    status: data?.status,
    id: data?.id,
  } as AssessmentReport & {
    published: string;
    status: string;
    id: string;
  };
}
